import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MousePointerClick, PenTool, MessageSquareQuote, Handshake, TrendingUp, Eye, Sparkles, Repeat, ArrowRight, Check, X, Ruler, Palette, Hash, ImagePlus, Building2, Star } from "lucide-react";
import { MarketingShell, Container, Reveal, Eyebrow, TrialButton, IMG, useSeo, FONT, JsonLd, SITE } from "./Marketing";
import { track } from "../../lib/analytics";

const STEPS = [
  { n: "01", icon: PenTool, title: "Customer designs their turf", body: "Visitors pick their size, colours, markings and logo — or let AI generate a complete concept from their branding in seconds." },
  { n: "02", icon: MessageSquareQuote, title: "Customer submits an enquiry", body: "The moment they love their design, they enter their details to request a quote — captured as a qualified lead." },
  { n: "03", icon: Handshake, title: "You receive a hot lead", body: "Every design lands in your dashboard and inbox with the exact spec, size and estimated price. You close the project." },
];

const BENEFITS = [
  { icon: TrendingUp, title: "Increase quote enquiries", body: "Interactive design turns passive browsers into people who've already committed to a concept — and want a price." },
  { icon: Eye, title: "Help customers visualise", body: "No more guesswork. Customers see a real, to-scale representation of their turf before they ever pay." },
  { icon: Sparkles, title: "Stand out from competitors", body: "Be the turf company with the tool nobody else has. An AI designer positions you as the premium option." },
  { icon: Repeat, title: "Reduce back-and-forth", body: "Designs arrive fully specced, cutting the endless email tennis and revision cycles before a sale." },
];

const INDUSTRIES = [
  { title: "Gym Flooring", img: IMG.gymFloor },
  { title: "Functional Fitness", img: IMG.fitness },
  { title: "Sports Facilities", img: IMG.arena },
  { title: "Turf Installers", img: IMG.turf },
];

const WITHOUT = [
  "Customers struggle to visualise the final design",
  "Sales teams spend hours creating concepts",
  "Endless revisions and back-and-forth",
  "Customers lose excitement during the buying process",
];
const WITH_IT = [
  "Customers create their own turf concepts",
  "Professional visuals generated instantly",
  "Capture customer details automatically",
  "Increase confidence and improve conversions",
];
const BUILT_FOR = [
  "Gym turf installers",
  "HYROX facilities",
  "Commercial gyms",
  "Sports flooring companies",
  "Athletic performance centres",
  "Sports academies",
];
const HERO_BULLETS = [
  "No design software needed",
  "Generate professional turf concepts in minutes",
  "Capture more qualified enquiries",
];

const LOGOS = ["ProTurf Installations", "Apex Gym Flooring", "Elite Sports Surfaces", "Northgate Turf Co.", "IronClad Fitness Floors"];

const TESTIMONIALS = [
  { quote: "We used to spend hours mocking up concepts for every enquiry. Now customers design it themselves and we just turn up to close the sale.", name: "Marcus Ellery", role: "Sales Director", company: "Apex Gym Flooring", initials: "ME" },
  { quote: "Our quote-to-order rate jumped the moment customers could actually see their turf before buying. Fewer tyre-kickers, more paid projects.", name: "Sophie Nadeau", role: "Owner", company: "ProTurf Installations", initials: "SN" },
  { quote: "It paid for itself on the first project. Every lead lands fully specced with the size, colours and markings — no back-and-forth.", name: "Daniel Ross", role: "Founder", company: "Elite Sports Surfaces", initials: "DR" },
];

export default function Home() {
  useSeo("AI Turf Designer — Let Customers Design Their Dream Gym Turf Before They Buy",
    "Add an AI-powered turf designer to your website. Let customers design custom gym turf, capture qualified enquiries and close more flooring projects. Start your 14-day free trial.");
  useEffect(() => { track("homepage_view", { page: "homepage" }); }, []);

  return (
    <MarketingShell>
      <JsonLd id="ld-home" data={{
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "AI Turf Designer",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "url": SITE.url,
        "description": SITE.description,
        "offers": { "@type": "AggregateOffer", "priceCurrency": "GBP", "lowPrice": "89.99", "highPrice": "399.99", "offerCount": 4 },
      }} />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMG.heroGym} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/85 to-[#0a0a0a]" />
        </div>
        <Container className="relative py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>Turn More Turf Enquiries Into Paid Orders</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-5 text-5xl md:text-7xl font-bold uppercase tracking-normal leading-[1.05] text-white" style={{ fontFamily: FONT.display }}>
                Let Customers Design Their <span className="text-[#00D26A]">Dream Gym Turf</span> Before They Buy
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
                Stop losing custom turf enquiries because customers can't visualise the finished design. Give your customers an interactive AI turf designer that creates professional concepts, captures qualified leads and helps them buy with confidence.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <TrialButton source="hero" testid="hero-start-trial" />
                <a href="/demo" data-testid="hero-try-demo" onClick={() => track("demo_cta_clicked", { source: "hero" })}
                  className="inline-flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md border border-white/20 text-white hover:bg-white/5 transition-colors">
                  Try The Live Demo <MousePointerClick size={16} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <ul className="mt-8 space-y-2.5 text-sm text-zinc-300" data-testid="hero-bullets">
                {HERO_BULLETS.map((b) => (
                  <li key={b} className="flex items-center gap-2.5">
                    <Check size={16} className="text-[#00D26A] shrink-0" /><span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <BrowserMock img="/customiser-hero.png" />
          </Reveal>
        </Container>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="problem-solution">
        <Container>
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white max-w-4xl leading-[1.03]" style={{ fontFamily: FONT.display }}>
              Custom turf sales are hard when customers can't see the finished result
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="mt-6 space-y-4 max-w-3xl text-zinc-300 text-lg leading-relaxed">
              <p>Most customers struggle to imagine how their finished gym turf, sprint track or training area will look. That means more questions, more revisions and more lost opportunities.</p>
              <p><span className="text-white font-semibold">AI Turf Designer</span> lets customers create their own concepts instantly — giving them confidence to move forward and helping turf companies close more projects.</p>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-xl border border-red-500/20 bg-red-500/[0.04] p-8" data-testid="without-card">
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-red-400/90">Without AI Turf Designer</div>
                <ul className="mt-6 space-y-4">
                  {WITHOUT.map((t) => (
                    <li key={t} className="flex gap-3 text-zinc-300"><X size={18} className="text-red-400 mt-0.5 shrink-0" /><span>{t}</span></li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-xl border border-[#00D26A]/30 bg-[#00D26A]/[0.05] p-8" data-testid="with-card">
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#00D26A]">With AI Turf Designer</div>
                <ul className="mt-6 space-y-4">
                  {WITH_IT.map((t) => (
                    <li key={t} className="flex gap-3 text-white"><Check size={18} className="text-[#00D26A] mt-0.5 shrink-0" /><span>{t}</span></li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-20 md:py-28 border-b border-white/10 bg-[#0d0d0d]" data-testid="social-proof-section">
        <Container>
          <Reveal>
            <div className="text-center text-xs uppercase tracking-[0.28em] text-zinc-500" data-testid="installer-logos-label">Trusted by turf &amp; flooring installers</div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4" data-testid="installer-logos">
              {LOGOS.map((l) => (
                <span key={l} className="text-lg md:text-xl font-black uppercase tracking-tight text-zinc-600 hover:text-zinc-300 transition-colors" style={{ fontFamily: FONT.display }}>{l}</span>
              ))}
            </div>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure className="h-full flex flex-col bg-[#161616] border border-white/5 rounded-xl p-7 hover:border-[#00D26A]/40 transition-colors" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-1 text-[#00D26A]">
                    {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={15} className="fill-[#00D26A]" />)}
                  </div>
                  <blockquote className="mt-5 text-zinc-200 leading-relaxed flex-1">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t border-white/5">
                    <span className="grid place-items-center w-11 h-11 rounded-full bg-[#00D26A]/15 text-[#00D26A] font-bold text-sm shrink-0" style={{ fontFamily: FONT.display }}>{t.initials}</span>
                    <span>
                      <span className="block text-white font-semibold text-sm leading-tight">{t.name}</span>
                      <span className="block text-zinc-500 text-xs mt-0.5">{t.role}, {t.company}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12 text-center">
              <TrialButton source="social-proof" testid="social-proof-start-trial" />
            </div>
          </Reveal>
        </Container>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-white/10 bg-[#0d0d0d]">
        <Container className="py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
          <span>Built for</span>
          {["Turf Installers", "Gym Flooring Co.", "Sports Flooring Suppliers", "Fitness Facility Designers"].map((t) => (
            <span key={t} className="text-zinc-400 font-semibold">{t}</span>
          ))}
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32" data-testid="how-it-works">
        <Container>
          <Reveal><Eyebrow>How it works</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 text-3xl md:text-5xl font-black uppercase tracking-tight text-white max-w-2xl leading-none" style={{ fontFamily: FONT.display }}>From website visitor to qualified lead in three steps</h2></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="group h-full bg-[#161616] border border-white/5 rounded-xl p-8 transition-transform duration-200 hover:-translate-y-1 hover:border-[#00D26A]/40">
                  <div className="flex items-center justify-between">
                    <span className="text-5xl font-black text-white/10 group-hover:text-[#00D26A]/30 transition-colors" style={{ fontFamily: FONT.display }}>{s.n}</span>
                    <s.icon className="text-[#00D26A]" size={26} />
                  </div>
                  <h3 className="mt-6 text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: FONT.display }}>{s.title}</h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed text-sm">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-28 bg-[#0d0d0d] border-y border-white/10" data-testid="benefits-section">
        <Container>
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <Reveal><Eyebrow>Why installers use it</Eyebrow></Reveal>
              <Reveal delay={0.05}><h2 className="mt-4 text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-none" style={{ fontFamily: FONT.display }}>A designer that pays for itself in one project</h2></Reveal>
              <Reveal delay={0.1}><p className="mt-5 text-zinc-400 leading-relaxed">Give customers a reason to engage — and give yourself higher-intent leads with fewer wasted quotes.</p></Reveal>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.06}>
                  <div className="h-full bg-[#161616] border border-white/5 rounded-xl p-6 hover:border-[#00D26A]/40 transition-colors">
                    <b.icon className="text-[#00D26A]" size={24} />
                    <h3 className="mt-4 text-lg font-bold uppercase tracking-tight text-white" style={{ fontFamily: FONT.display }}>{b.title}</h3>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{b.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* BUILT FOR TURF COMPANIES */}
      <section className="py-24 md:py-28" data-testid="built-for-section">
        <Container className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Not another generic AI tool</Eyebrow></Reveal>
            <Reveal delay={0.05}><h2 className="mt-4 text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-none" style={{ fontFamily: FONT.display }}>Built specifically for turf installers</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-zinc-400 leading-relaxed text-lg">Unlike generic AI image tools, AI Turf Designer is built around the real sales process of custom turf businesses.</p></Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {BUILT_FOR.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="flex items-center gap-4 rounded-xl border border-white/5 bg-[#161616] p-6 hover:border-[#00D26A]/40 transition-colors h-full" data-testid={`built-for-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#00D26A]/10 shrink-0"><Building2 size={20} className="text-[#00D26A]" /></span>
                  <span className="text-white font-semibold leading-tight">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* DEMO CTA / SHOWCASE */}
      <section className="py-24 md:py-32" data-testid="showcase-section">
        <Container className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal><BrowserMock large /></Reveal>
          <div>
            <Reveal><Eyebrow>See it in action</Eyebrow></Reveal>
            <Reveal delay={0.05}><h2 className="mt-4 text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-none" style={{ fontFamily: FONT.display }}>Play with the real designer — no signup</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-zinc-400 leading-relaxed text-lg">Try the exact tool your customers would use. Pick a size, choose colours and markings, and watch AI turn a logo into a complete turf concept — with an instant price.</p></Reveal>
            <Reveal delay={0.16}>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="/demo" data-testid="showcase-try-demo" onClick={() => track("demo_cta_clicked", { source: "showcase" })}
                  className="inline-flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md bg-[#00D26A] text-black hover:bg-[#00E574] transition-colors">
                  Try The Live Demo <ArrowRight size={16} />
                </a>
                <TrialButton source="showcase" variant="secondary">Start Free Trial</TrialButton>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-28 bg-[#0d0d0d] border-y border-white/10" data-testid="industries-section">
        <Container>
          <Reveal><Eyebrow>Who it's for</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-none" style={{ fontFamily: FONT.display }}>Made for the flooring & turf industry</h2></Reveal>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.title} delay={i * 0.06}>
                <div className="group relative overflow-hidden rounded-xl border border-white/10 aspect-[3/4]" data-testid={`industry-${ind.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <img src={ind.img} alt={ind.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <h3 className="text-lg font-bold uppercase tracking-tight text-white leading-none" style={{ fontFamily: FONT.display }}>{ind.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32" data-testid="final-cta">
        <Container>
          <div className="relative overflow-hidden rounded-2xl border border-[#00D26A]/30 bg-gradient-to-br from-[#0f1a12] to-[#0d0d0d] p-10 md:p-16 text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#00D26A]/15 blur-3xl rounded-full" />
            <div className="relative">
              <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]" style={{ fontFamily: FONT.display }}>Ready to turn browsers into leads?</h2>
              <p className="mt-5 text-zinc-300 text-lg max-w-xl mx-auto">Launch your own branded AI turf designer in minutes. Start free for 14 days.</p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                <TrialButton source="final-cta" testid="final-start-trial" />
                <Link to="/contact" className="inline-flex items-center justify-center font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md border border-white/20 text-white hover:bg-white/5 transition-colors" data-testid="final-book-demo">Book a Demo</Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </MarketingShell>
  );
}

const MOCK_CONTROLS = [
  [Ruler, "Dimensions", "10m × 1.5m"],
  [Palette, "Turf colour", "Black / Red"],
  [Hash, "Markings", "Metre numbers"],
  [ImagePlus, "Logo", "STRONGMAN GYM"],
];

function BrowserMock({ large = false, img = null }) {
  return (
    <div className={`relative rounded-xl border border-white/10 bg-[#111] overflow-hidden shadow-2xl ${large ? "" : "lg:rotate-1"}`} data-testid="designer-mock">
      <div className="flex items-center gap-1.5 px-4 h-9 bg-[#161616] border-b border-white/10">
        <span className="w-3 h-3 rounded-full bg-red-500/70" /><span className="w-3 h-3 rounded-full bg-yellow-500/70" /><span className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-3 text-[11px] text-zinc-500">your-turf-company.com/designer</span>
      </div>
      <div className="relative aspect-[16/11] bg-[#0d0d0d]">
        {img ? (
          <img src={img} alt="AI turf designer customiser with live turf preview" className="absolute inset-0 w-full h-full object-contain object-center" />
        ) : (
        <div className="absolute inset-0 flex">
          {/* Customer controls */}
          <div className="hidden sm:flex w-[42%] flex-col gap-2.5 p-4 bg-[#0f0f0f] border-r border-white/10 overflow-hidden">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Customer chooses</div>
            {MOCK_CONTROLS.map(([Icon, label, val]) => (
              <div key={label} className="flex items-center gap-2.5 rounded-lg bg-[#161616] border border-white/5 px-3 py-2">
                <Icon size={15} className="text-[#00D26A] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none">{label}</div>
                  <div className="text-xs text-white font-semibold truncate mt-0.5">{val}</div>
                </div>
              </div>
            ))}
            <div className="flex gap-1.5 mt-1">
              {["#141414", "#d51f26", "#ededed", "#00D26A"].map((c) => <span key={c} className="w-5 h-5 rounded border-2 border-white/30" style={{ background: c }} />)}
            </div>
          </div>
          {/* AI-generated concept + enquiry */}
          <div className="relative flex-1">
            <img src={IMG.turfStripe} alt="AI-generated turf concept" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/85 via-transparent to-[#0a0a0a]/40" />
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/70 backdrop-blur border border-[#00D26A]/40 rounded-full px-3 py-1">
              <Sparkles size={12} className="text-[#00D26A]" /><span className="text-[10px] uppercase tracking-widest text-[#00D26A] font-semibold">AI concept</span>
            </div>
            <div className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-white/10 rounded-lg px-3 py-1.5">
              <div className="text-[9px] uppercase tracking-widest text-[#00D26A]">Live Price</div>
              <div className="text-lg font-black text-white leading-none mt-0.5" style={{ fontFamily: FONT.display }}>£2,005</div>
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <span className="block text-center bg-[#00D26A] text-black text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded">Get My Quote</span>
            </div>
          </div>
        </div>
        )}
      </div>
      {/* Flow caption: from blank turf to finished design */}
      <div className="border-t border-white/10 bg-[#0f0f0f] px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-wider">
          <span className="text-zinc-400">Design</span>
          <ArrowRight size={12} className="text-[#00D26A]" />
          <span className="text-zinc-400">AI generates</span>
          <ArrowRight size={12} className="text-[#00D26A]" />
          <span className="text-[#00D26A] font-semibold">Enquiry</span>
        </div>
        <div className="text-[10px] text-zinc-500 mt-1.5">From blank turf to finished design in minutes</div>
      </div>
    </div>
  );
}
